import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Alert from '@/components/Alert';
import { Logo } from '@/components/Logo';
import { ResetPasswordForm } from '@/components/ResetPasswordForm';
import { useTheme } from '@/components/ThemeProvider';
import { Button } from '@/components/ui/button';
import Translator from 'components/i18n/Translator';

import { ChainlitContext, useAuth } from 'client-types/*';

export const ResetPasswordError = new Error(
  'Error resetting password. Please try again later.'
);

export default function ResetPassword() {
  const { token } = useParams<{ token: string }>();
  const { data: config, user } = useAuth();
  const [success, setSuccess] = useState(false);
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  const { variant } = useTheme();
  const isDarkMode = variant === 'dark';

  const handleResetPassword = async (
    resetToken: string,
    newPassword: string
  ) => {
    try {
      const response = await apiClient.resetPassword(resetToken, newPassword);
      return response;
    } catch (error: any) {
      throw new Error(
        error.detail || error.message || 'Failed to reset password'
      );
    }
  };

  const handleSuccess = () => {
    setSuccess(true);
    // Auto-redirect to login after 3 seconds
    setTimeout(() => {
      navigate(
        '/login?success=Password has been reset successfully. Please sign in.'
      );
    }, 3000);
  };

  useEffect(() => {
    // Only redirect if config is loaded and user is authenticated
    if (config && user) {
      navigate('/');
    }
  }, [config, user]);

  // Handle missing token
  if (!token) {
    return (
      <div className="grid min-h-svh lg:grid-cols-2">
        <div className="flex flex-col gap-4 p-6 md:p-10">
          <div className="flex justify-center gap-2 md:justify-start">
            <Logo className="w-[150px]" />
          </div>
          <div className="flex flex-1 items-center justify-center">
            <div className="w-full max-w-xs text-center">
              <h1 className="text-2xl font-bold mb-4">
                <Translator path="auth.resetPassword.invalidLink.title" />
              </h1>
              <Alert variant="error">
                <div className="text-center">
                  <p className="text-sm">
                    <Translator path="auth.resetPassword.invalidLink.description" />
                  </p>
                </div>
              </Alert>
              <div className="mt-6 space-y-3">
                <Button
                  onClick={() => navigate('/forgot-password')}
                  className="w-full"
                >
                  <Translator path="auth.resetPassword.invalidLink.requestNew" />
                </Button>
                <Link
                  to="/login"
                  className="block text-sm text-primary hover:underline font-medium"
                >
                  <Translator path="auth.resetPassword.invalidLink.backToLogin" />
                </Link>
              </div>
            </div>
          </div>
        </div>
        {!config?.headerAuth ? (
          <div className="relative hidden bg-muted lg:block overflow-hidden">
            <img
              src={
                config?.ui?.login_page_image ||
                apiClient.buildEndpoint('/favicon')
              }
              alt="Image"
              className={`absolute inset-0 h-full w-full object-cover ${
                isDarkMode
                  ? config?.ui?.login_page_image_dark_filter ||
                    'brightness-[0.2] grayscale'
                  : config?.ui?.login_page_image_filter || ''
              }`}
            />
          </div>
        ) : null}
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="grid min-h-svh lg:grid-cols-2">
        <div className="flex flex-col gap-4 p-6 md:p-10">
          <div className="flex justify-center gap-2 md:justify-start">
            <Logo className="w-[150px]" />
          </div>
          <div className="flex flex-1 items-center justify-center">
            <div className="w-full max-w-xs text-center">
              <Alert variant="success">
                <h2 className="text-lg font-semibold mb-2">
                  <Translator path="auth.resetPassword.success.title" />
                </h2>
                <p className="mb-4">
                  <Translator path="auth.resetPassword.success.description" />
                </p>
              </Alert>
              <Button
                onClick={() => navigate('/login')}
                className="w-full mt-4"
              >
                <Translator path="auth.resetPassword.success.backToLogin" />
              </Button>
            </div>
          </div>
        </div>
        {!config?.headerAuth ? (
          <div className="relative hidden bg-muted lg:block overflow-hidden">
            <img
              src={
                config?.ui?.login_page_image ||
                apiClient.buildEndpoint('/favicon')
              }
              alt="Image"
              className={`absolute inset-0 h-full w-full object-cover ${
                isDarkMode
                  ? config?.ui?.login_page_image_dark_filter ||
                    'brightness-[0.2] grayscale'
                  : config?.ui?.login_page_image_filter || ''
              }`}
            />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <Logo className="w-[150px]" />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            <ResetPasswordForm
              token={token}
              onResetPassword={handleResetPassword}
              onSuccess={handleSuccess}
            />
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                <Translator path="auth.forgotPassword.actions.backToLogin" />{' '}
                <Link
                  to="/login"
                  className="text-primary hover:underline font-medium"
                >
                  <Translator path="auth.login.alternativeText.backToLogin" />
                </Link>
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                <Translator path="auth.resetPassword.form.needNewCode" />{' '}
                <Link
                  to="/forgot-password"
                  className="text-primary hover:underline font-medium"
                >
                  <Translator path="auth.resetPassword.form.requestNewReset" />
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
      {!config?.headerAuth ? (
        <div className="relative hidden bg-muted lg:block overflow-hidden">
          <img
            src={
              config?.ui?.login_page_image ||
              apiClient.buildEndpoint('/favicon')
            }
            alt="Image"
            className={`absolute inset-0 h-full w-full object-cover ${
              isDarkMode
                ? config?.ui?.login_page_image_dark_filter ||
                  'brightness-[0.2] grayscale'
                : config?.ui?.login_page_image_filter || ''
            }`}
          />
        </div>
      ) : null}
    </div>
  );
}
